import React, { useEffect, useState } from "react";
import api from "../../api/axiosClient";
import { safeFormatDate } from "../../common/dateUtils";

interface Service {
  id: number;
  name: string;
}

interface ServicePrice {
  id: number;
  serviceId: number;
  serviceName?: string;
  price: number;
  effectiveFrom: string;
  makeupArtistId?: number;
}

export default function ArtistServicePrices() {
  const [services, setServices] = useState<Service[]>([]);
  const [prices, setPrices] = useState<ServicePrice[]>([]);
  const [selectedService, setSelectedService] = useState<number | "">("");
  const [price, setPrice] = useState("");
  const [effectiveFrom, seteffectiveFrom] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getArtistId = () => {
    if (typeof window === 'undefined') return 0;
    const raw = localStorage.getItem('makeupArtistId');
    return raw ? Number(raw) : 0;
  };

  // Load services for selection
  const loadServices = async () => {
    try {
      const res = await api.get<Service[]>("/Service");
      setServices(res.data || []);
    } catch (err) {
      setError("Failed to load services.");
    }
  };

  // Load artist-specific prices
  const loadPrices = async () => {
    try {
      // Prefer artist-scoped endpoint; fall back to query param for compatibility
      const artistId = getArtistId();
      let res;
      try {
        res = await api.get<ServicePrice[]>("/ServicePrice/artist");
      } catch {
        res = await api.get<ServicePrice[]>(`/ServicePrice?artistId=${artistId}`);
      }
      setPrices(res.data || []);
    } catch (err) {
      setError("Failed to load prices.");
    }
  };

  useEffect(() => {
    loadServices();
    loadPrices();
  }, []);

  const addPrice = async () => {
    if (!selectedService || !price || !effectiveFrom) {
      setError("All fields are required.");
      return;
    }
    const artistId = getArtistId();
    if (!artistId) {
      setError("Missing makeup artist identity. Please re-login.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      await api.post("/ServicePrice", {
        serviceId: selectedService,
        price: parseFloat(price),
        effectiveFrom,
        makeupArtistId: artistId,
      });
      setPrice("");
      seteffectiveFrom("");
      setSelectedService("");
      loadPrices();
    } catch (err: any) {
      const msg = err?.response?.data || err?.message || "Failed to add service price.";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const deletePrice = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this price?")) return;
    try {
      setLoading(true);
      await api.delete(`/ServicePrice/${id}`);
      setPrices((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError("Failed to delete price.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4">My Service Prices</h2>

      {/* Add New Price */}
      <div className="grid md:grid-cols-4 gap-4 items-end mb-6">
        <div>
          <label className="block mb-1 font-medium">Service</label>
          <select
            value={selectedService}
            onChange={(e) => setSelectedService(Number(e.target.value))}
            className="border border-gray-300 rounded px-3 py-2 w-full"
          >
            <option value="">Select Service</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-1 font-medium">Price</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Enter price"
            className="border border-gray-300 rounded px-3 py-2 w-full"
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Effective Date</label>
          <input
            type="date"
            value={effectiveFrom}
            onChange={(e) => seteffectiveFrom(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-full"
          />
        </div>

        <button
          onClick={addPrice}
          disabled={loading}
          className="btn-primary mt-1 disabled:bg-gray-400"
        >
          {loading ? "Saving..." : "Add Price"}
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* Price List */}
      {prices.length === 0 ? (
        <p className="text-gray-500">No service prices found.</p>
      ) : (
        <table className="w-full border border-gray-200 bg-white rounded-lg shadow-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Service</th>
              <th className="px-4 py-2 text-left">Price</th>
              <th className="px-4 py-2 text-left">Effective Date</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {prices.map((p) => (
              <tr key={p.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2">{p.serviceName || "-"}</td>
                <td className="px-4 py-2">₹ {p.price.toFixed(2)}</td>
                <td className="px-4 py-2">{safeFormatDate(p.effectiveFrom)}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => deletePrice(p.id)}
                    className="text-red-500 hover:text-red-700 font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
